import { useState } from 'react'
import { Link } from 'react-router-dom'
import { personalInfo } from '@/data/personal'
import { cn } from '@/lib/utils'
import { useSeo } from '@/hooks/useSeo'
import { trackEvent } from '@/lib/analytics'

export function AboutPage() {
  useSeo(
    'About',
    'Optimization and ML engineer in Washington, DC. Background, how I work, and what I am currently building.'
  )
  const [imgFailed, setImgFailed] = useState(false)
  const initials = personalInfo.displayName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')

  return (
    <div className="container max-w-3xl pb-20 pt-12">
      <header className="mb-8 border-b border-border/40 pb-8">
        <h1 className="text-3xl font-semibold tracking-tight">About</h1>
      </header>

      <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
        <div
          className={cn(
            'relative h-56 w-56 shrink-0 overflow-hidden rounded-full border border-border/60 bg-muted',
            'mx-auto sm:mx-0'
          )}
        >
          {/* Initials sit behind the photo: shown if the image is missing,
              fails, or (like a blank export) is fully transparent. A real
              photo simply paints over them. */}
          <span className="absolute inset-0 flex items-center justify-center text-4xl font-medium text-muted-foreground">
            {initials}
          </span>
          {!imgFailed && (
            <img
              src={personalInfo.avatar}
              alt={personalInfo.name}
              className="relative h-full w-full object-cover"
              onError={() => setImgFailed(true)}
            />
          )}
        </div>

        <div className="flex-1 space-y-4 leading-relaxed text-muted-foreground">
          <p>
            I&rsquo;m Chris. I work on the decisions large organizations
            make without really looking at them.
          </p>
          <p>
            Every big company has a handful of choices that move
            staggering amounts of money and material. Which factory makes
            what. How a billion units get routed. What goes into a
            container, and in what order. You&rsquo;d assume these are
            heavily engineered. Mostly they&rsquo;re made by a few
            experienced people with a spreadsheet and good instincts. The
            instincts are usually decent, which is exactly why nobody
            interrogates the process.
          </p>
          <p>
            I find that gap fascinating and a little alarming. The math
            itself is mostly solved already, sitting in libraries anyone
            can download. The actual gap is elsewhere: turning a decision
            into a model means learning the decision first, building what
            feeds it, building what someone actually opens, and convincing
            a room of people to trust a number over their own judgment.
            That&rsquo;s four jobs. Almost nobody wants all four. I want
            all four.
          </p>
          <p>
            I&rsquo;ve done this for sneakers and for munitions, which has
            taught me that the domain changes less than you&rsquo;d think.
          </p>
          <p>
            If you&rsquo;re working on something where the decision
            matters more than the model, I&rsquo;d like to hear about it.
          </p>
          <p>
            The{' '}
            <Link
              to="/resume"
              className="text-foreground underline-offset-4 hover:underline"
            >
              resume
            </Link>{' '}
            has the full history. If you want to get in touch,{' '}
            <a
              href={personalInfo.contact.linkedin}
              target="_blank"
              rel="noreferrer"
              className="text-foreground underline-offset-4 hover:underline"
              onClick={() => trackEvent('social_click', { platform: 'linkedin', location: 'about' })}
            >
              LinkedIn
            </a>{' '}
            is the best place to find me.
          </p>
        </div>
      </div>
    </div>
  )
}
